import { useEffect, useState } from 'react';
import { formatDateTime } from '@awj/i18n';
import { Skeleton } from '@awj/ui';

import { Pager } from '~/components/Pager.tsx';
import { EmptyState } from '~/components/EmptyState.tsx';
import { ExportMenu } from '~/components/ExportMenu.tsx';
import { Select } from '~/components/Select.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { auditActionLabel } from '~/lib/audit-action.ts';
import { auditColumns } from '~/lib/export-columns.ts';
import { allAuditEntries, useAudit } from '~/lib/settings-queries.ts';

/*
 * Prefixes rather than every action name: "auth" is the question someone actually asks.
 *
 * Each one is the start of real action codes. "Products" used to send `product`, but product and
 * category changes are recorded as `catalog.*` — so that area was always empty, and reviews,
 * enquiries and content, which are audited too, had no area at all.
 */
const AREAS = ['auth', 'catalog', 'order', 'customer', 'review', 'inquiry', 'content', 'staff', 'setting'] as const;

/**
 * The audit trail.
 *
 * Read-only, and it looks it: no row action, nothing to click, nothing to correct. An audit
 * trail that can be edited is not one, and a panel that offers a delete button beside each entry
 * invites the question of whether it ever was.
 */
export function AuditPanel() {
  const { t, locale } = useI18n();

  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [debouncedActor, setDebouncedActor] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedActor(actor);
      setPage(1);
    }, 350);

    return () => clearTimeout(timer);
  }, [actor]);

  const trail = useAudit({ action, actor: debouncedActor, page });

  return (
    <>
      <div className="filter-bar">
        <div className="filter-bar__row">
          <label className="filter-bar__search">
            <span className="sr-only">{t('settings.auditActor')}</span>
            <input
              className="filter-bar__search-input"
              type="search"
              value={actor}
              placeholder={t('settings.auditActor')}
              onChange={(event) => setActor(event.target.value)}
            />
          </label>

          <div className="filter-bar__controls">
            <div className="filter-bar__field">
              <label htmlFor="filter-area">{t('settings.auditArea')}</label>
              <Select
                id="filter-area"
                value={action}
                onChange={(next) => {
                  setAction(next);
                  setPage(1);
                }}
                placeholder={t('settings.auditAllAreas')}
                options={AREAS.map((area) => ({
                  value: area,
                  label: t(`auditArea.${area}` as 'auditArea.auth'),
                }))}
              />
            </div>
          </div>

          <div className="filter-bar__actions">
            <ExportMenu
              file="audit-log"
              sheet={t('settings.audit')}
              columns={auditColumns(t)}
              rows={() => allAuditEntries({ action, actor: debouncedActor })}
              hint={t('export.allMatching')}
              disabled={!trail.data?.total}
            />
          </div>
        </div>
      </div>

      {trail.isPending || !trail.data ? (
        <Skeleton height="320px" />
      ) : trail.data.items.length === 0 ? (
        <EmptyState icon="clock" title={t('settings.auditNone')} tall />
      ) : (
        <>
          <div className="data-table">
  <div className="data-table__scroll" tabIndex={0} role="region" aria-label={t('settings.audit')}>
              <table className="data-table__table data-table--dense">
                <thead>
                  <tr>
                    <th className="data-table__th" scope="col">{t('settings.auditWhen')}</th>
                    <th className="data-table__th" scope="col">{t('settings.auditAction')}</th>
                    <th className="data-table__th" scope="col">{t('settings.auditWho')}</th>
                    <th className="data-table__th" scope="col">{t('settings.auditWhat')}</th>
                    <th className="data-table__th" scope="col">{t('settings.auditFrom')}</th>
                  </tr>
                </thead>
                <tbody>
                  {trail.data.items.map((entry) => (
                    <tr key={entry.id}>
                      <td className="data-table__td">
                        <bdi>{formatDateTime(locale, entry.occurredAt)}</bdi>
                      </td>
                      <td className="data-table__td">
                        {/* A name a person reads; the code stays in the tooltip for searching. */}
                        <span title={entry.action}>{auditActionLabel(entry.action, t)}</span>
                        {entry.summary ? (
                          <span className="data-table__sub">
                            <bdi>{entry.summary}</bdi>
                          </span>
                        ) : null}
                      </td>
                      <td className="data-table__td">
                        {entry.actorEmail ? (
                          <bdi>{entry.actorEmail}</bdi>
                        ) : (
                          // No actor means it was not a person: a sweep, a webhook, a failed
                          // sign-in for an address nobody has.
                          <span className="data-table__sub">{t('orders.bySystem')}</span>
                        )}
                      </td>
                      <td className="data-table__td">
                        {entry.entityName ? (
                          <span className="mono">
                            {entry.entityName}
                            {entry.entityId ? ` #${entry.entityId}` : ''}
                          </span>
                        ) : (
                          <span className="data-table__sub">—</span>
                        )}
                      </td>
                      <td className="data-table__td">
                        <span className="mono">{entry.ipAddress ?? '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
</div>

          <Pager
            page={trail.data.page}
            totalPages={trail.data.totalPages ?? 1}
            total={trail.data.total}
            onPage={setPage}
          />
        </>
      )}
    </>
  );
}
